import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Box,
  Tooltip,
  IconButton
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createColor,
  deleteColor,
  getAllColor,
  updateColor,
} from "../../State/Color/Action";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Add, Close, Delete, Edit, FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";


const AddColor = () => {
  const dispatch = useDispatch();
  const { colors } = useSelector((store) => store);
  const [colorData, setColorData] = useState({ name: "", hexCode: "" });
  const [editingId, setEditingId] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  useEffect(() => {
    dispatch(getAllColor());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setColorData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (colorData.name.trim() === "") {
      toast.error("Please fill all the fields");
      return;
    }

    if (colorData.name.length < 3 || colorData.name.length > 20) {
      toast.error("Color name should be between 3 to 20 characters");
      return;
    }

    if (editingId) {
      dispatch(updateColor(editingId, colorData));
    } else {
      dispatch(createColor(colorData));
    }

    handleCancle();
  };

  const handleConfirmDelete = () => {
    dispatch(deleteColor(colorToDelete));
    setOpenConfirm(false);
    setColorToDelete(null);
  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
    setColorToDelete(null);
  };

  const handleColorDelete = (colorId) => {
    setColorToDelete(colorId);
    setOpenConfirm(true);
  };


  const handleColorUpdate = (item) => {
    setColorData({ name: item.name, hexCode: item.hexCode });
    setEditingId(item.id);
    setOpenFormModal(true);
  };

  const handleCancle = () => {
    setColorData({
      name: "", hexCode: ""
    });
    setEditingId(null);
    setOpenFormModal(false);
  }

  const writeAction = PermissionService.has({ category: "Admin", page: "Colors", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "name", headerName: "Color Name", flex: 1 },
    {
      field: "hexCode",
      headerName: "Hex Code",
      flex: 1,
      renderCell: (params) => (params.value || "----"),
    },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleColorUpdate(params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleColorDelete(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    ] : []),
  ];

  const filteredRows = colors?.colors?.response?.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(query.toLowerCase()) ||
      row.hexCode?.toLowerCase().includes(query.toLowerCase())
    );
  });

    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1, row.name, row.hexCode || "----"].join(',')).join('\n');
    const headers = ['S.No.', 'Color Name', 'Hex Code'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'colors.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Color List", 14, 15);
    const tableColumn = ['S.No.', 'Color Name', 'Hex Code'];
    const tableRows = exportRows.map((row, index) => [index + 1, row.name, row.hexCode || "----"]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('colors.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Color Name', 'Hex Code'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.name, row.hexCode || '----']);
    const html = `
            <html>
              <head>
                <title>Color List</title>
                <style>
                  table { border-collapse: collapse; width: 100%; }
                  th, td { border: 1px solid black; padding: 8px; text-align: left; }
                  th { background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;}
                  tr:nth-child(even) { background-color: #f5f5f5; }
                </style>
              </head>
              <body>
                <h2>Color List</h2>
                <table>
                  <thead>
                    <tr>
                      ${headers.map(header => `<th>${header}</th>`).join('')}
                    </tr>
                  </thead>
                  <tbody>
                    ${tableContent.map(row => `
                      <tr>
                        ${row.map(cell => `<td>${cell}</td>`).join('')}
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </body>
            </html>
          `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div >

      <Card sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6" className="text-gray-700">COLORS</Typography>}
            sx={{ p: 0 }}
          />
          {writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenFormModal(true);
                  setEditingId(null);
                }}
              >
                <Add />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                Add Color
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
          </Box>
          <Box>
            <TextField
              label="Search Colors"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
          </Box>
        </Box>
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            getRowId={(row) => row.id}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={colors.loading}
            autoHeight={true}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                padding: "8px",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </Card>
      <Dialog open={openConfirm} onClose={handleCancelDelete}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this color?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace the form rendering with a Dialog: */}
      <Dialog
        open={openFormModal}
        onClose={handleCancle}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {editingId ? "UPDATE" : "ADD NEW"} COLOR
          <IconButton onClick={handleCancle} size="small" sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Color Name"
                      name="name"
                      value={colorData.name}
                      onChange={handleChange}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hex Code"
                      name="hexCode"
                      value={colorData.hexCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase(); // uppercase me convert
                        if (/^[0-9A-F]*$/.test(value) && value.length <= 6) {
                          handleChange(e); // sirf valid hone par update
                        }
                      }}
                      inputProps={{
                        maxLength: 6, // max 6
                      }}
                      error={
                        colorData.hexCode !== "" && colorData.hexCode !== null && colorData.hexCode.length !== 6
                      }
                      helperText={
                        colorData.hexCode !== "" && colorData.hexCode !== null && colorData.hexCode.length !== 6
                          ? "Hex code must be exactly 6 characters"
                          : ""
                      }
                      size="medium"
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={colorData.hexCode !== "" && colorData.hexCode !== null && colorData.hexCode.length !== 6}
                      sx={{
                        background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                          boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                          transform: "scale(1.02)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      {editingId ? "Update " : "Add "} Color
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancle}
                      sx={{
                        color: "#449AEF",
                        borderColor: "#449AEF",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#449AEF",
                          color: "#fff",
                          borderColor: "#449AEF",
                          boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AddColor;
